// 3rd party imports
import * as React from 'react';

// mobx
import { Quicklink } from 'models';

// Shared UI
import { useLocalization, LocalizedText } from '@shared-ui/localization-context';

// ui
import { UitkIcon } from 'uitk-react-icons';
import { UitkCard } from 'uitk-react-cards';
import { UitkFakeInput } from 'uitk-react-input';
import { UitkLink } from 'uitk-react-link';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';

// components
import { CopyButton } from './copy-button';

// models
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';
import { LinkType } from 'models/analytics/enums';

interface ConfiguredQuicklinkProps extends Quicklink {
  index: number;
  link: string;
  title: string;
  name: string;
  pzLink: string;
  code: string;
  analyticsTrackEvents?: (rffr: string, link: LinkAnalyticsMicroMessageContent) => void;
}

export const ConfiguredQuicklink: React.FC<ConfiguredQuicklinkProps> = ({
  index,
  link,
  title,
  name,
  pzLink,
  code,
  analyticsTrackEvents,
}) => {
  const l10n = useLocalization();

  return (
    <UitkSpacing margin={{ blockend: 'four' }}>
      <UitkCard padded={true}>
        <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
          <>
            <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
              <div>
                <UitkHeading tag="h2" size={5}>
                  {title}
                </UitkHeading>
                <UitkLink inline={true}>
                  <a target="_blank" href={link}>
                    <LocalizedText message="product.quicklink.configured.link.preview" />
                    <UitkIcon name="open_in_new" />
                  </a>
                </UitkLink>
              </div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
              <UitkLayoutGrid columns={{ small: 1, large: 2 }} space="four">
                <UitkLayoutGridItem>
                  <div>
                    <UitkLayoutFlex
                      space="two"
                      className="QuicklinkInputs"
                      data-stid={`copy-preconfigured-link-${index}`}
                    >
                      <UitkFakeInput
                        id={`link-${name}`}
                        label={l10n.formatText('product.quicklink.form.link')}
                        value={pzLink}
                      />
                      <CopyButton
                        value={pzLink}
                        onCopyClick={() =>
                          analyticsTrackEvents('affhub_copy_link.selected', {
                            linkType: LinkType.PREDEFINED,
                            linkIndex: index,
                            linkTargetUrl: pzLink,
                          })
                        }
                      />
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutGridItem>
                <UitkLayoutGridItem>
                  <div>
                    <UitkLayoutFlex
                      space="two"
                      className="QuicklinkInputs"
                      data-stid={`copy-preconfigured-code-${index}`}
                    >
                      <UitkFakeInput
                        id={`code-${name}`}
                        label={l10n.formatText('product.quicklink.form.code')}
                        value={code}
                      />
                      <CopyButton
                        value={code}
                        onCopyClick={() =>
                          analyticsTrackEvents('affhub_copy_code.selected', {
                            linkType: LinkType.PREDEFINED,
                            linkIndex: index,
                            linkTargetUrl: code,
                          })
                        }
                      />
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </UitkLayoutGridItem>
          </>
        </UitkLayoutGrid>
      </UitkCard>
    </UitkSpacing>
  );
};
